import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, HelpCircle, User, LogOut, ArrowLeft } from "lucide-react";
import { useAuth } from "@/features/auth/index.js";
import { AppShell } from "@/shared/ui/AppShell.js";
import { BrandLogo } from "@/shared/ui/BrandLogo.js";
import { Button } from "@/shared/ui/button.js";
import { cn } from "@/shared/lib/utils.js";

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";

/**
 * Marco común para las páginas de cuenta (Perfil y Manual). Reusa AppShell:
 * riel lateral con navegación real + cabecera editorial con logo, usuario y salir.
 * El destino «Informes» depende del rol (admin → /admin, técnico → /tecnico).
 */
export function AccountShell({
  active,
  children,
}: {
  active: "perfil" | "manual";
  children: ReactNode;
}) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const home = user?.rol === "admin" ? "/admin" : "/tecnico";
  const rolLabel = user?.rol === "admin" ? "Administrador" : "Técnico";

  return (
    <AppShell
      nav={[
        { icon: FileText, label: "Informes", onClick: () => navigate(home) },
        { icon: User, label: "Mi perfil", active: active === "perfil", onClick: () => navigate("/perfil") },
        { icon: HelpCircle, label: "Manual", active: active === "manual", onClick: () => navigate("/manual") },
      ]}
    >
      <header className="sticky top-0 z-10 border-b border-border bg-card">
        <div className="flex items-center justify-between px-6 py-4 md:px-8">
          <div className="flex items-center gap-4">
            <BrandLogo height={32} />
            <span aria-hidden className="hidden h-8 w-px bg-border sm:block" />
            <span className={cn(labelClass, "hidden sm:block")}>
              {active === "perfil" ? "Mi perfil" : "Manual de usuario"}
            </span>
          </div>
          <div className="flex items-center gap-5">
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-foreground">{user?.nombre}</div>
              <div className={labelClass}>{rolLabel}</div>
            </div>
            <Button
              variant="outline"
              onClick={logout}
              className="gap-2 font-mono text-xs uppercase tracking-[0.12em]"
            >
              <LogOut className="size-4" aria-hidden />
              Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto w-full max-w-3xl px-6 py-8 md:px-8 md:py-10">
        {/* Volver al panel — visible también en móvil donde el riel se oculta. */}
        <button
          type="button"
          onClick={() => navigate(home)}
          className="mb-6 inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.12em] text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Volver al panel
        </button>
        {children}
      </main>
    </AppShell>
  );
}
